import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Edit3, Save, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

// ============================================================
// SuperAdminOrderOverride — Feb 2026
// Universal Super Admin Override panel for ANY order type (branch,
// local customer, export). Renders:
//   * amber banner reminding the operator they are overriding locks
//   * "Edit fields" toggle → expands into an editable form
//   * form → posts to `PATCH /admin/orders/{order_id}` with the
//     whitelisted fields
//
// The component NO-OP renders (returns null) when the caller is not
// the super admin, so hosting pages can drop it in unconditionally.
// ============================================================
const SuperAdminOrderOverride = ({ order, onSaved }) => {
  const { authHeaders, isSuperAdmin } = useAdminAuth();
  const { toast } = useToast();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  if (!isSuperAdmin || !order?.id) return null;

  const startEdit = () => {
    setForm({
      customer_business_name: order.customer_business_name || '',
      customer_name: order.customer_name || '',
      customer_email: order.customer_email || '',
      customer_phone: order.customer_phone || '',
      customer_address: order.customer_address || '',
      customer_number: order.customer_number || '',
      notes: order.notes || '',
      status: order.status || '',
      is_export_order: !!order.is_export_order,
      export_country_code: order.export_country_code || '',
      export_country_name: order.export_country_name || '',
    });
    setEdit(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.id}`,
        form,
        { headers: authHeaders },
      );
      toast({
        title: 'Order updated',
        description: (data?.changed_fields || []).length
          ? `Updated: ${(data.changed_fields || []).join(', ')}`
          : 'No changes',
      });
      setEdit(false);
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!edit) {
    return (
      <div
        className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-2"
        data-testid={`super-admin-banner-${order.id}`}
      >
        <Shield className="w-3.5 h-3.5 text-amber-700 shrink-0" />
        <span className="text-[11px] text-amber-900 flex-1">
          <b>Super Admin Override active</b> — every field editable, all status locks bypassed.
        </span>
        <button
          type="button"
          onClick={startEdit}
          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 px-2.5 py-0.5 text-[10px] font-bold"
          data-testid={`super-admin-edit-toggle-${order.id}`}
        >
          <Edit3 className="w-3 h-3" /> Edit fields
        </button>
      </div>
    );
  }

  return (
    <div
      className="mb-3 rounded-xl border border-amber-300 bg-amber-50/60 p-3 space-y-2"
      data-testid={`super-admin-panel-${order.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> Super Admin Edit Mode
        </div>
        <button
          type="button"
          onClick={() => setEdit(false)}
          className="text-[11px] text-neutral-500 hover:text-neutral-900"
          data-testid={`super-admin-edit-cancel-${order.id}`}
        >
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field label="Business Name">
          <Input
            value={form.customer_business_name}
            onChange={(e) => setForm({ ...form, customer_business_name: e.target.value })}
            data-testid={`super-admin-business-${order.id}`}
          />
        </Field>
        <Field label="Contact Person">
          <Input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            data-testid={`super-admin-contact-${order.id}`}
          />
        </Field>
        <Field label="Email">
          <Input
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            data-testid={`super-admin-email-${order.id}`}
          />
        </Field>
        <Field label="Phone">
          <Input
            value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
            data-testid={`super-admin-phone-${order.id}`}
          />
        </Field>
        <Field label="Customer Number">
          <Input
            value={form.customer_number}
            onChange={(e) => setForm({ ...form, customer_number: e.target.value })}
            data-testid={`super-admin-customer-number-${order.id}`}
          />
        </Field>
        <Field label="Order Status">
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid={`super-admin-status-${order.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['new', 'confirmed', 'assigned', 'picked_up', 'out_for_delivery', 'delivered', 'received', 'shipped', 'cancelled', 'failed', 'draft'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Address">
            <Input
              value={form.customer_address}
              onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
              data-testid={`super-admin-address-${order.id}`}
            />
          </Field>
        </div>
        <Field label="Export order?">
          <div className="flex items-center gap-2 h-9">
            <input
              type="checkbox"
              checked={!!form.is_export_order}
              onChange={(e) => setForm({ ...form, is_export_order: e.target.checked })}
              className="w-4 h-4"
              data-testid={`super-admin-is-export-${order.id}`}
            />
            <span className="text-xs text-neutral-600">Flag order as export</span>
          </div>
        </Field>
        {form.is_export_order && (
          <>
            <Field label="Country Code">
              <Input
                value={form.export_country_code}
                onChange={(e) => setForm({ ...form, export_country_code: e.target.value.toUpperCase() })}
                data-testid={`super-admin-country-code-${order.id}`}
              />
            </Field>
            <Field label="Country Name">
              <Input
                value={form.export_country_name}
                onChange={(e) => setForm({ ...form, export_country_name: e.target.value })}
                data-testid={`super-admin-country-name-${order.id}`}
              />
            </Field>
          </>
        )}
        <div className="md:col-span-2">
          <Field label="Order Notes">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid={`super-admin-notes-${order.id}`}
            />
          </Field>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="rounded-full h-8 text-xs bg-amber-700 hover:bg-amber-800 text-white"
          data-testid={`super-admin-save-${order.id}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save Order
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-0.5">{label}</label>
    {children}
  </div>
);

export default SuperAdminOrderOverride;
